import { useQuery, useMutation } from '@tanstack/react-query';
import { API_URL, getAuthHeaders, useNotification } from '../../hooks';
import { useAuth0 } from '@auth0/auth0-react';

type StreamingQuestion = {
  question?: string;
};

const isStreamingQuestion = (data: unknown): data is StreamingQuestion => {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('question' in data ? typeof data.question === 'string' : true)
  );
};

const parseStreamingQuestions = async (
  reader: ReadableStreamDefaultReader<Uint8Array>
) => {
  const decoder = new TextDecoder('utf-8');
  let result = '';
  const dataItems: Array<StreamingQuestion> = [];

  let continueReading = true;
  while (continueReading) {
    const { done, value } = await reader.read();
    if (done) {
      continueReading = false;
      break;
    }

    result += decoder.decode(value, { stream: true });

    result.split('\n').forEach((line) => {
      if (line.startsWith('data: ') && !line.endsWith('done')) {
        try {
          const parsedJson = JSON.parse(line.slice(6));
          if (isStreamingQuestion(parsedJson)) {
            dataItems.push(parsedJson);
          }
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      }
    });
  }

  return dataItems;
};

export const getQuestions = async (
  getAccessTokenSilently: ReturnType<typeof useAuth0>['getAccessTokenSilently']
) => {
  const token = await getAccessTokenSilently();

  const response = await fetch(`${API_URL}/gitbook-chatbot/questions`, {
    headers: getAuthHeaders(token),
  });

  const reader = response.body?.getReader();
  if (reader) {
    const questions = await parseStreamingQuestions(reader);
    return questions
      .filter(
        (item): item is { question: string } =>
          typeof item.question === 'string'
      )
      .map((item) => item.question);
  }

  return [];
};

export const useGitbookRecommendedQuestions = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  const { apiError } = useNotification();
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    enabled,
    retry: false,
    queryKey: ['recommendedQuestions'],
    queryFn: () => getQuestions(getAccessTokenSilently),
    refetchOnWindowFocus: false,
    staleTime: 30 * 60 * 1000, // 30 minutes
    onError: apiError,
  });
};

type Answer = {
  followupQuestions?: string[];
  answer?: {
    markdown?: string;
  };
};

type Source = {
  type?: string;
  space?: string;
  revision?: string;
  page?: string;
  sections?: string[];
  reason?: string;
};

type StreamingAnswer = {
  type?: string;
  answer?: Answer;
  sources?: Array<Source>;
};

const parseStreamingAnswer = async (
  reader: ReadableStreamDefaultReader<Uint8Array>
) => {
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  const dataItems: Array<StreamingAnswer> = [];

  let continueReading = true;

  while (continueReading) {
    const { done, value } = await reader.read();
    if (done) {
      continueReading = false;
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    // Process complete JSON objects
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer

    for (const line of lines) {
      if (line.startsWith('data: ') && !line.endsWith('done')) {
        try {
          const jsonStr = line.slice(6); // Remove 'data: ' prefix
          const data = JSON.parse(jsonStr);
          if (typeof data === 'object' && data !== null) {
            dataItems.push(data);
          }
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      }
    }
  }

  // Process any remaining data in the buffer
  if (buffer) {
    if (buffer.startsWith('data: ') && !buffer.endsWith('done')) {
      try {
        const jsonStr = buffer.slice(6); // Remove 'data: ' prefix
        const data = JSON.parse(jsonStr);
        if (typeof data === 'object' && data !== null) {
          dataItems.push(data);
        }
      } catch (error) {
        console.error('Error parsing JSON:', error);
      }
    }
  }

  return dataItems;
};

type GitbookAskResult = {
  answer: string;
  followupQuestions: string[];
};

const getAnswer = async (
  question: string,
  getAccessTokenSilently: ReturnType<typeof useAuth0>['getAccessTokenSilently']
): Promise<GitbookAskResult> => {
  const token = await getAccessTokenSilently();
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(token),
  };
  const response = await fetch(`${API_URL}/gitbook-chatbot/ask`, {
    headers,
    method: 'POST',
    body: JSON.stringify({
      question,
    }),
  });

  const reader = response?.body?.getReader();

  if (!reader) {
    throw new Error('No reader');
  }

  const answers = await parseStreamingAnswer(reader);

  // Get the last complete answer
  const lastAnswer = answers.length > 0 ? answers[answers.length - 1] : null;
  return {
    answer: lastAnswer?.answer?.answer?.markdown || '',
    followupQuestions: lastAnswer?.answer?.followupQuestions || [],
  };
};

export const useGitbookAsk = () => {
  const { apiError } = useNotification();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<GitbookAskResult, Error, string>({
    mutationKey: ['askQuestion'],
    mutationFn: (question) => getAnswer(question, getAccessTokenSilently),
    onError: apiError,
  });
};
